import { useState, useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FilterAlt } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridFilterModel, GridCallbackDetails, GridToolbar, getGridStringOperators, GridPrintGetRowsToExportParams, GridRowId, selectedGridRowsSelector, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-premium';
import Loading from '../../components/Loading';
import NoDataAvailable from "../../components/NoDataAvailable";
import { fetchQuickSiteListByModule } from "../../api/quickSite";
import { useSnackbar } from 'notistack';
import { QUICKSIGHT_MODULE, RIMS_COOKIE_STG_CONSTANTS, RIMS_SESSION_STG_CONSTANTS } from "../../constants";
import cookie from "react-cookies";
import { getSpecifyRelatedResourceList } from "../../api/admin";
import { useDataGridLocalText } from "../../hook/useDataGridLocalText";
import CustomToolbarAudit from "../../components/CustomToolbarAudit";
import { uniqueArray } from "../../common/utils";
import DataGridPaginationSlot from '../../components/DataGridPaginationSlot';
import { GRID_PAGE_SIZE_OPTIONS } from '../../components/GridPagination';

const Report = () => {
  const { t } = useTranslation();
  const { localeText } = useDataGridLocalText();
  const [reportList, setReportList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [''],
  });
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const columns: GridColDef[] = [
    {
      field: 'displayModuleName',
      headerName: t('report.module'),
      flex: 1,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        return (
          <Tooltip title={cellValues.value} placement="top-start"><div>{cellValues.value}</div></Tooltip>
        );
      },
      filterOperators: getGridStringOperators().filter((operator) => operator.value !== "isAnyOf"),
    },
    {
      field: 'type',
      headerName: t('report.reportType'),
      flex: 1,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        return (
          <Tooltip title={cellValues.value} placement="top-start"><div>{cellValues.value}</div></Tooltip>
        );
      },
      filterOperators: getGridStringOperators().filter((operator) => operator.value !== "isAnyOf"),
    },
    {
      field: 'name',
      headerName: t('report.reportName'),
      flex: 1,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        const isGroup = cellValues.rowNode.type === "group";

        return (
          <Tooltip title={cellValues.value} placement="top">
            {!isGroup ? 
              <Link className="text-ellipsis" to={`${window.location.origin}/reports/quicksight-reports/${cellValues.row.id}`} target="_blank">{cellValues.value}</Link> :
              <div>{cellValues.value}</div>}
          </Tooltip>
        );
      },
      filterOperators: getGridStringOperators().filter((operator) => operator.value !== "isAnyOf"),
    },
  ];

  const fetchQuickSightList = async () => {
    const fetchApiModuleName = [QUICKSIGHT_MODULE.ADMIN_CENTER];
    const tenantId =
          sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID) ||
          cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID);

    const results = await Promise.all([
      fetchQuickSiteListByModule("x", QUICKSIGHT_MODULE.ADMIN_CENTER).catch((err: any) => {
        return {
          code: 1000,
          data: []
        }
      }),
      ...fetchApiModuleName.map((i: any) => fetchQuickSiteListByModule(tenantId, i).catch((err: any) => {
        return {
          code: 1000,
          data: []
        }
      }))
    ]);

    const allResult = [].concat(...results.map((result: any) => {
      return result.data.map((data: any) => {
        return {
          ...data,
          id: data.dashboard_id,
          module: QUICKSIGHT_MODULE.ADMIN_CENTER,
          type: t('common.dashboard'),
          url: '',
          displayModuleName: t('common.ac'),
        }
      });
    }));
    return uniqueArray([...allResult], "id");
  }

  const fetchPrivilegeRes = async () => {
    return getSpecifyRelatedResourceList({
      userId: cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_ID),
      resourceType: "data",
      app: "",
      module: "",
      subModule: "",
      parentId: null,
    }).then((res: any) => {
      if (res.code === 200) {
        return res.data;
      }
    }).catch((err: any) => {
      if (err?.response?.status == 401 || err?.response?.data?.message === "unauthorized") {
        return;
      };
      enqueueSnackbar(err.response.data.message, { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    }).finally(() => {
    });
  }

  const getSelectedRowsToExport = ({
    apiRef,
  }: GridPrintGetRowsToExportParams): GridRowId[] => {
    const selectedRowIds = selectedGridRowsSelector(apiRef);
    if (selectedRowIds.size > 0) {
      return Array.from(selectedRowIds.keys());
    }

    return gridFilteredSortedRowIdsSelector(apiRef);
  };

  useEffect(() => {
    Promise.all([fetchQuickSightList(), fetchPrivilegeRes()]).then(([quickSightList, privilegeRes]) => {
      function mergeRestrictions(data: any) {
        const mergedData = data.reduce((acc: any, curr: any) => {
          if (curr.restriction && curr.name === 'quick_sight_restriction') {
            const restrictionListData = JSON.parse(curr.restriction);
            restrictionListData.forEach((item: any) => {
              const existingModule = acc.find((mod: any) => mod.moduleName === item.moduleName);
              if (existingModule) {
                //@ts-ignore
                existingModule.restrictionList = [...new Set([...existingModule.restrictionList, ...item.restrictionList])];
              } else {
                acc.push(item);
              }
            });
          }
          return acc;
        }, []);

        return mergedData;
      }
      const havePrivilegeList = mergeRestrictions(privilegeRes);

      function filterPrivilegeData(allList: any[], privilegeRestrictionList: any[]): any[] {
        const privilegeList: any[] = [];
        privilegeRestrictionList.forEach((item: any) => {
          const moduleName = item.moduleName;
          const restrictionList = item.restrictionList;
          const filtered = allList.filter((data: any) => data.module === moduleName && restrictionList.includes(data.name));
          privilegeList.push(...filtered);
        });
        return privilegeList;
      }

      const privilegeFilterList = filterPrivilegeData(quickSightList, havePrivilegeList);
      //@ts-ignore
      setReportList(privilegeFilterList);
    }).catch((err: any) => {
      // if (err?.response?.status == 401 || err?.response?.data?.message === "unauthorized") {
      //   return;
      // };
      // enqueueSnackbar(err.response.data.message, { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    }).finally(() => {
      setLoading(false);
    })
  }, []);

  return <>
    <Box className="main-app-innerpage">
      <Typography className="page-title pagetitle-line34" variant="h1">{t(`menu.platformRelatedReports`)}</Typography>
      <Box>
        <Box sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
          <DataGridPremium
            disableAggregation
            rows={reportList}
            columns={columns}
            columnHeaderHeight={32}
            className="react-table"
            rowHeight={38}
            disableRowSelectionOnClick
            filterModel={filterModel}
            onFilterModelChange={(
              filterModel: GridFilterModel,
              details: GridCallbackDetails<'filter'>
            ) => {
              setFilterModel(filterModel)
            }}
            paginationModel={{ page: pageNum, pageSize: pageSize }}
            onPaginationModelChange={(paginationModel: any) => { setPageSize(paginationModel.pageSize); setPageNum(paginationModel.page); }}
            pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
            pagination
            components={{
              ColumnMenuIcon: () => { return (<FilterAlt></FilterAlt>) },
            }}
            sx={{
              '& .MuiDataGrid-toolbarContainer': { flexDirection: 'row-reverse' },
              '& .MuiDataGrid-pinnedColumnHeaders': { paddingRight: '0!important' }
            }}
            slots={{
              pagination: DataGridPaginationSlot,
              toolbar: CustomToolbarAudit,
              noRowsOverlay: () => <NoDataAvailable></NoDataAvailable>,
            }}
            localeText={localeText}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: { getRowsToExport: getSelectedRowsToExport },
                utf8WithBom: true,
                csvOptions: {
                  utf8WithBom: true,
                },
                entityType: "Admin Center Report",
                moduleName: "Reports",
                entityName: "Admin Center Report"
              },
            }}
          />
        </Box>
      </Box>
      <Loading open={loading} />
    </Box>
  </>;
}

export default Report;
